import { LoadingState, useApi, type MyChildResponse } from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import { useMyChildren } from '../myChildren';

/*
 * The links the server writes on a notification, in the shapes this app has no screen for. A push carries them
 * to the service worker (Q68), and a tap opens them after the nursery's sign-in, so each one has to land somewhere.
 * The server's link names the record, not this app's layout, and the server matches on it (opening a thread marks
 * its notifications read), so the link is translated here rather than changed there. Each of these finds the child
 * the record sits under and goes there; `deepLinks.test.tsx` holds every shape the server writes to a screen.
 */

/** The child a link with no child of its own opens under: the first one whose screens are all open. */
function firstChild(children: MyChildResponse[] | undefined): number | null {
  const child = children?.find((c) => !c.paymentsOnly) ?? children?.[0];
  return child?.childId ?? null;
}

/**
 * `/messages/{conversationId}`: the thread, under the child it is about. The thread is read by its id, not looked
 * for in the list, whose first page an older thread has fallen off.
 */
export function ThreadLink() {
  const api = useApi();
  const id = Number(useParams().conversationId);
  const named = Number.isInteger(id) && id > 0;
  const children = useMyChildren();
  const thread = useQuery({
    queryKey: ['conversations', id],
    queryFn: () => api.conversations.get(id),
    enabled: named,
    retry: false,
  });

  if (children.isPending || (named && thread.isPending)) return <LoadingState />;

  const child = thread.data?.childContextId ?? firstChild(children.data);
  if (child === null) return <Navigate to="/" replace />;
  return <Navigate to={`/child/${child}/messages${named ? `?conversation=${id}` : ''}`} replace />;
}

/** `/complaints/{complaintId}`, raised without a child: the complaint, under the first child. */
export function ComplaintLink() {
  const api = useApi();
  const id = Number(useParams().complaintId);
  const children = useMyChildren();
  const complaint = useQuery({ queryKey: ['complaints', id], queryFn: () => api.complaints.get(id), retry: false });

  if (children.isPending || complaint.isPending) return <LoadingState />;

  const child = complaint.data?.childId ?? firstChild(children.data);
  if (child === null) return <Navigate to="/" replace />;
  return <Navigate to={complaint.isSuccess ? `/child/${child}/complaints/${id}` : `/child/${child}/complaints`} replace />;
}

/** `/announcements/…`: a guardian reads an announcement in the notification centre, which no child narrows. */
export function NotificationsLink() {
  const children = useMyChildren();
  if (children.isPending) return <LoadingState />;

  const child = firstChild(children.data);
  return <Navigate to={child === null ? '/' : `/child/${child}/notifications`} replace />;
}
